import React, { useState, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Chip,
  Pagination,
  Skeleton,
  useTheme,
} from "@mui/material";
import RequestReviewDialog from "../../../components/RequestReviewDialog/RequestReviewDialog";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useQuery } from "@apollo/client";
import {
  GET_EVIDENCES_BY_CHECK,
  GET_AI_EVIDENCE_OWNERS_FOR_CHECK,
} from "../../../apis/integrated-evidence/aiEvidenceApis";
import AIEvidenceAccessRequest from "./AIEvidenceAccessRequest";
import AIEvidenceLockedStrip from "./AIEvidenceLockedStrip";
import EntityDetailDialog from "../../IntegratedEvidence/EntityDetailDialog";
import { IntegratedEvidenceApprovalStatusChip } from "../../IntegratedEvidence/IntegratedEvidenceApprovalStatusChip";
import AIEvidenceTableContainer from "./common/AIEvidenceTableContainer";
import { EmptyFields } from "../../../utils/enums";
import {
  FEATURE_IDS,
  useFeatureEnabled,
} from "../../../components/AccessControl";

const formatEntityType = (type) =>
  (type || "")
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const AICheckEvidenceSuggestions = ({ activeCheckId }) => {
  const theme = useTheme();
  const tc = theme.customColors;
  const aiEvidenceEnabled = useFeatureEnabled(
    FEATURE_IDS.REGO_AI_EVIDENCE_COLLECTION
  );
  const [tableExpanded, setTableExpanded] = useState(true);
  const [entityQueue, setEntityQueue] = useState([]);
  const [queueIndex, setQueueIndex] = useState(0);
  const [detailOpen, setDetailOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  const {
    data,
    previousData,
    loading,
    refetch,
  } = useQuery(GET_EVIDENCES_BY_CHECK, {
    variables: { checkId: activeCheckId, page, pageSize },
    skip: !aiEvidenceEnabled || !activeCheckId,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  // network-only empties `data` while a page is in flight; fall back to the
  // previous page's result so rows and pagination stay mounted.
  const shown = data ?? previousData;
  const backendEntities = shown?.getEvidencesByCheck?.entities || [];
  const totalCount = shown?.getEvidencesByCheck?.totalCount || 0;
  const noAccess = !loading && totalCount > 0 && backendEntities.length === 0;

  const { data: ownersData } = useQuery(GET_AI_EVIDENCE_OWNERS_FOR_CHECK, {
    variables: { checkId: activeCheckId },
    skip: !aiEvidenceEnabled || !activeCheckId || loading,
    fetchPolicy: "cache-first",
  });
  const owners = useMemo(
    () => ownersData?.getAIEvidenceOwnersForCheck || [],
    [ownersData]
  );
  const hiddenCount = useMemo(
    () => owners.reduce((sum, o) => sum + (o.pendingCount || 0), 0),
    [owners]
  );
  const totalPages = Math.ceil(totalCount / pageSize);
  const allEntities = backendEntities;
  const pendingEntities = useMemo(
    () => allEntities.filter((e) => e.approvalStatus === "pending"),
    [allEntities]
  );
  const reviewedEntities = useMemo(
    () => allEntities.filter((e) => e.approvalStatus !== "pending"),
    [allEntities]
  );
  const pendingCount = totalCount;

  const tableRows = useMemo(() => {
    return [...pendingEntities, ...reviewedEntities];
  }, [pendingEntities, reviewedEntities]);

  React.useEffect(() => {
    setPage(1);
  }, [activeCheckId]);

  const openDetail = useCallback(
    (row) => {
      const { entityId, entityType } = row;
      const rest = tableRows
        .filter((e) => e.entityId !== entityId && e.approvalStatus === "pending")
        .map((e) => ({ id: e.entityId, type: e.entityType }));
      setEntityQueue([{ id: entityId, type: entityType }, ...rest]);
      setQueueIndex(0);
      setDetailOpen(true);
    },
    [tableRows]
  );

  const handleNext = useCallback(() => {
    setQueueIndex((idx) => {
      const next = idx + 1;
      if (next >= entityQueue.length) {
        setDetailOpen(false);
        return 0;
      }
      return next;
    });
  }, [entityQueue.length]);

  const handlePrev = useCallback(() => {
    setQueueIndex((idx) => Math.max(0, idx - 1));
  }, []);

  const tableTitle = useMemo(() => {
    const shimmerTitleSx = {
      fontWeight: 700,
      fontSize: "0.95rem",
      letterSpacing: "-0.01em",
      background: `linear-gradient(90deg, ${tc.text.strong} 40%, #3B82F6 50%, ${tc.text.strong} 60%)`,
      backgroundSize: "200% auto",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      animation: "aiShimmer 4s linear infinite",
    };
    if (loading || pendingCount === 0 || noAccess) {
      return (
        <Typography variant="h5" sx={shimmerTitleSx}>
          Rego AI Evidence Suggestions
        </Typography>
      );
    }
    const pendingLabel =
      pendingCount === 1 ? "1 pending review" : `${pendingCount} pending review`;
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap", minWidth: 0 }}>
        <Typography variant="h5" component="span" sx={shimmerTitleSx}>
          Rego AI Evidence Suggestions
        </Typography>
        <Chip
          label={pendingLabel}
          size="small"
          sx={{
            height: 24,
            fontSize: "0.7rem",
            fontWeight: 600,
            border: "none",
            borderRadius: "999px",
            backgroundColor: tc.status.info.bg,
            color: tc.status.info.text,
            pointerEvents: "none",
            "& .MuiChip-label": { px: 1.25 },
          }}
        />
      </Box>
    );
  }, [loading, pendingCount, noAccess, tc]);

  if (!aiEvidenceEnabled) return null;

  if (noAccess && owners.length > 0) {
    return (
      <AIEvidenceTableContainer
        title={tableTitle}
        tableExpanded={tableExpanded}
        onToggleExpand={() => setTableExpanded((v) => !v)}
        loading={false}
      >
        <AIEvidenceAccessRequest
          checkId={activeCheckId}
          totalCount={totalCount}
          owners={owners}
        />
      </AIEvidenceTableContainer>
    );
  }

  // Placeholder rows, shown on the first load and again on every page change.
  const skeletonRows = (count) => (
    <Box sx={{ px: 2.5, py: 2 }}>
      {Array.from({ length: count }, (_, i) => (
        <Box
          key={i}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            py: 1.25,
            borderBottom:
              i < count - 1 ? `1px solid ${tc.border.subtle}` : "none",
          }}
        >
          <Skeleton variant="circular" width={26} height={26} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Skeleton
              variant="text"
              width={`${70 - (i % 3) * 12}%`}
              height={16}
            />
            <Skeleton
              variant="text"
              width={`${45 - (i % 3) * 8}%`}
              height={12}
            />
          </Box>
          <Skeleton
            variant="rounded"
            width={72}
            height={24}
            sx={{ borderRadius: "999px" }}
          />
        </Box>
      ))}
    </Box>
  );

  // First load: nothing to show yet, so the skeleton stands in for the section.
  if (loading && allEntities.length === 0) {
    return (
      <AIEvidenceTableContainer
        title={tableTitle}
        tableExpanded={tableExpanded}
        onToggleExpand={() => setTableExpanded((v) => !v)}
        loading={false}
      >
        {skeletonRows(3)}
      </AIEvidenceTableContainer>
    );
  }

  if (allEntities.length === 0 && hiddenCount === 0) return null;

  return (
    <>
      <AIEvidenceTableContainer
        title={tableTitle}
        tableExpanded={tableExpanded}
        onToggleExpand={() => setTableExpanded((v) => !v)}
        loading={false}
      >
        {/* Evidence you cannot see yet leads the section — it is the item that
            needs an action, so it should not be buried under the rows. */}
        {hiddenCount > 0 && (
          <>
            <Box
              sx={{
                px: 2.5,
                pt: 1.75,
                pb: 0.75,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: tc.text.disabled,
                }}
              >
                Managed by others
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  height: "1px",
                  backgroundColor: tc.border.subtle,
                }}
              />
            </Box>
            <AIEvidenceLockedStrip checkId={activeCheckId} owners={owners} />
          </>
        )}

        {hiddenCount > 0 && tableRows.length > 0 && (
          <Box
            sx={{
              px: 2.5,
              pt: 1.75,
              pb: 0.75,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: tc.text.disabled,
              }}
            >
              Yours to review
            </Typography>
            <Box
              sx={{ flex: 1, height: "1px", backgroundColor: tc.border.subtle }}
            />
          </Box>
        )}

        {/* Paging swaps the rows for skeletons. Only this block is replaced —
            the pagination below stays mounted so the controls never vanish. */}
        {loading && skeletonRows(3)}

        <Box sx={{ p: 1, display: loading ? "none" : "block" }}>
          {tableRows.map((row, idx) => {
            const isPending = row.approvalStatus === "pending";
            const fullName = row.entityName?.trim() ? row.entityName : EmptyFields.UNKNOWN;
            const typeLabel = formatEntityType(row.entityType);

            return (
              <Box
                key={row.entityId || idx}
                onClick={() => openDetail(row)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  px: 2,
                  py: 1.5,
                  mx: 0.5,
                  my: 0.5,
                  borderRadius: "12px",
                  cursor: "pointer",
                  position: "relative",
                  animation: "aiFadeSlideIn 0.3s ease forwards",
                  animationDelay: `${idx * 0.05}s`,
                  opacity: 0,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: isPending
                      ? "rgba(59, 130, 246, 0.04)"
                      : tc.background.hover,
                    "& .ai-row-arrow": {
                      transform: "translateX(3px)",
                      color: "#3B82F6",
                    },
                  },
                }}
              >
                {/* Status dot for pending */}
                {isPending && (
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 20,
                      height: 20,
                      position: "relative",
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        borderRadius: "50%",
                        background: "radial-gradient(circle, rgba(59,130,246,0.6) 0%, rgba(59,130,246,0.3) 40%, rgba(59,130,246,0.1) 70%, rgba(59,130,246,0) 100%)",
                        animation: "aiRadialPulse 2s ease-out infinite",
                        "@keyframes aiRadialPulse": {
                          "0%": { transform: "scale(0.5)", opacity: 1 },
                          "50%": { transform: "scale(1.2)", opacity: 0.7 },
                          "100%": { transform: "scale(1.5)", opacity: 0 },
                        },
                      }}
                    />
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: "#3B82F6",
                        position: "relative",
                        zIndex: 1,
                      }}
                    />
                  </Box>
                )}

                {/* Content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        color: tc.text.strong,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        lineHeight: 1.3,
                      }}
                    >
                      {fullName}
                    </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: tc.text.secondary,
                      mt: 0.25,
                      lineHeight: 1.2,
                    }}
                  >
                    {typeLabel}
                  </Typography>
                </Box>

                {/* Status */}
                <Box sx={{ flexShrink: 0 }}>
                  <IntegratedEvidenceApprovalStatusChip approvalStatus={row.approvalStatus} />
                </Box>

                {/* Arrow */}
                <ArrowForwardIosIcon
                  className="ai-row-arrow"
                  sx={{
                    fontSize: 14,
                    color: tc.text.secondary,
                    flexShrink: 0,
                    transition: "all 0.2s ease",
                    opacity: 0.6,
                  }}
                />
              </Box>
            );
          })}

          {tableRows.length === 0 && hiddenCount === 0 && (
            <Box sx={{ textAlign: "center", py: 4, px: 2 }}>
              <Typography sx={{ color: tc.text.secondary, fontSize: "0.84rem" }}>
                No evidence suggestions yet
              </Typography>
            </Box>
          )}
        </Box>

        {totalPages > 1 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              pt: 2,
              pb: 2,
            }}
          >
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
            />
          </Box>
        )}
      </AIEvidenceTableContainer>

      <EntityDetailDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        entityId={entityQueue[queueIndex]?.id}
        entityType={entityQueue[queueIndex]?.type}
        contextCheckId={activeCheckId}
        onActionComplete={() => refetch()}
        onNext={queueIndex < entityQueue.length - 1 ? handleNext : undefined}
        onPrev={queueIndex > 0 ? handlePrev : undefined}
      />

      <RequestReviewDialog
        open={reviewDialogOpen}
        onClose={() => setReviewDialogOpen(false)}
        checkId={activeCheckId}
      />
    </>
  );
};

export default AICheckEvidenceSuggestions;
